<template>
  <div ref="rootElement" class="d-flex flex-column ga-3 w-100">
    <div
      v-for="tool in displayedToolCalls"
      :key="tool.toolExecutionRequestId"
      class="ai-tool-approval-card pa-4"
      :class="getStatusClass(tool)"
      :data-testid="`ai-tool-approval-card-${tool.toolExecutionRequestId}`"
    >
      <template v-if="isPending(tool)">
        <div class="body3 font-weight-bold">
          {{ $t("aiAssistant.toolApproval.title") }}
        </div>

        <div class="ai-tool-approval-divider"></div>

        <div class="d-flex flex-column ga-1 mb-3 body3">
          <div v-for="row in getApprovalRows(tool)" :key="row.key">
            <span class="font-weight-bold mr-1">{{ row.label }}</span>
            <span>{{ row.value }}</span>
          </div>
        </div>

        <div class="d-flex justify-end ga-2">
          <v-btn
            variant="outlined"
            color="primary"
            rounded="pill"
            :loading="isActionLoading('reject')"
            :disabled="isActionLoading('approve')"
            :data-testid="`ai-tool-approval-reject-${tool.toolExecutionRequestId}`"
            @click="handleAction('reject', tool)"
          >
            {{ $t("aiAssistant.toolApproval.reject") }}
          </v-btn>
          <v-btn
            variant="flat"
            color="primary"
            rounded="pill"
            class="text-white"
            :loading="isActionLoading('approve')"
            :disabled="isActionLoading('reject')"
            :data-testid="`ai-tool-approval-approve-${tool.toolExecutionRequestId}`"
            @click="handleAction('approve', tool)"
          >
            <v-icon icon="$quickFix" size="small" class="mr-1"></v-icon>
            {{ $t("aiAssistant.toolApproval.authorize") }}
          </v-btn>
        </div>
      </template>

      <div
        v-else-if="tool.status === ToolApprovalStatus.PERMISSION_DENIED"
        class="d-flex flex-column"
        :data-testid="`ai-tool-approval-permission-denied-${tool.toolExecutionRequestId}`"
      >
        <div
          class="body3 font-weight-bold"
          :data-testid="`ai-tool-approval-permission-denied-title-${tool.toolExecutionRequestId}`"
        >
          {{ $t("aiAssistant.toolApproval.permissionDenied") }}
        </div>
        <div
          class="body3"
          :data-testid="`ai-tool-approval-permission-denied-details-${tool.toolExecutionRequestId}`"
        >
          {{ $t("general.details") }}: {{ $t("aiAssistant.toolApproval.contactYourAdmin") }}
        </div>
      </div>

      <div
        v-else
        class="body3 font-weight-bold"
        :data-testid="`ai-tool-approval-status-${tool.toolExecutionRequestId}`"
      >
        {{ getStatusText(tool) }}
      </div>
    </div>
    <div
      v-if="hasPendingDisplayed"
      class="body3 text-center text-indigo-medium"
      v-html="hotkeysHint"
      data-testid="ai-tool-approval-hotkeys-hint"
    ></div>
    <div
      v-if="pendingOverflowCount > 0"
      class="body3 text-medium-emphasis text-center text-indigo-medium"
      data-testid="ai-tool-approval-pending-overflow"
    >
      {{
        $t(
          "aiAssistant.toolApproval.pendingMore",
          { n: pendingOverflowCount },
          pendingOverflowCount
        )
      }}
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, onUnmounted, type PropType, ref } from "vue";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import {
  type ToolApprovalCall,
  type ToolApprovalRequiredContent,
  useAiAssistantStore,
} from "@/_store/ai-assistant.module";
import { ToolApprovalStatus, ToolApprovalType } from "@/constants/ai-assistant.ts";
import { parseToolApprovalArguments } from "@/_helpers/ai-assistant.ts";
import { getOSVersion } from "@/_helpers/utils.ts";
import camelCase from "lodash/camelCase";

type ToolApprovalRow = {
  key: string;
  label: string;
  value: string;
};

export default defineComponent({
  props: {
    content: {
      type: Object as PropType<ToolApprovalRequiredContent>,
      required: true,
    },
  },
  setup(props) {
    const loadingAction = ref<"approve" | "reject" | null>(null);
    const rootElement = ref<HTMLElement | null>(null);
    const { t } = useI18n();
    const aiAssistantStore = useAiAssistantStore();
    const { currentSession } = storeToRefs(aiAssistantStore);

    const sessionId = computed(() => currentSession.value?.sessionId || "");

    const getApprovalRows = (tool: ToolApprovalCall): ToolApprovalRow[] => {
      return [
        {
          key: "action",
          label: t("aiAssistant.toolApproval.actionLabel"),
          value: getToolNameDisplayValue(tool),
        },
      ];
    };

    const getToolNameDisplayValue = (tool: ToolApprovalCall): string => {
      const parsedTool = parseToolApprovalArguments(tool);

      if (!parsedTool) {
        return "";
      }

      if (parsedTool.toolName === ToolApprovalType.DO_EVENT_ACTION) {
        const { eventId } = parsedTool.arguments;
        return `${eventId} Action`;

        //TODO: add support for actions when they will be ready on BE side
        // return `${eventId} - ${t(`ticketDetails.eventActions.${action}`)}`;
      }

      const actionTranslationMap: Partial<Record<ToolApprovalType, string>> = {
        [ToolApprovalType.FIX_SECURITY_GAP]: t("securityGaps.actions.fixAll", 1),
        [ToolApprovalType.IGNORE_SECURITY_GAP]: t("securityGaps.actions.ignore"),
        [ToolApprovalType.UNIGNORE_SECURITY_GAP]: t("securityGaps.actions.unignore"),
      };

      const isSecurityGapTool = [
        ToolApprovalType.FIX_SECURITY_GAP,
        ToolApprovalType.IGNORE_SECURITY_GAP,
        ToolApprovalType.UNIGNORE_SECURITY_GAP,
      ].includes(parsedTool.toolName);

      if (!isSecurityGapTool) {
        return parsedTool.toolName;
      }

      const actionLabel = actionTranslationMap[parsedTool.toolName];
      const { securityGapType } = parsedTool.arguments;

      if (!actionLabel || !securityGapType) {
        return parsedTool.toolName;
      }

      return `${actionLabel} ${t(`securityGaps.securityGapTypes.${camelCase(securityGapType)}.title`)}`;
    };

    const isPending = (tool: ToolApprovalCall): boolean =>
      tool.status === ToolApprovalStatus.PENDING;

    const statusClassMap: Partial<Record<ToolApprovalStatus, string>> = {
      [ToolApprovalStatus.EXECUTED]: "ai-tool-approval-approved",
      [ToolApprovalStatus.REJECTED]: "ai-tool-approval-neutral",
      [ToolApprovalStatus.FAILED]: "ai-tool-approval-neutral",
      [ToolApprovalStatus.PERMISSION_DENIED]: "ai-tool-approval-neutral",
    };

    const getStatusClass = (tool: ToolApprovalCall): string => statusClassMap[tool.status] || "";

    const getStatusText = (tool: ToolApprovalCall): string => {
      const statusesMap: Record<ToolApprovalStatus, () => string> = {
        [ToolApprovalStatus.EXECUTED]: () =>
          t("aiAssistant.toolApproval.approved", { toolName: getToolNameDisplayValue(tool) }),
        [ToolApprovalStatus.FAILED]: () => t("aiAssistant.toolApproval.failed"),
        [ToolApprovalStatus.REJECTED]: () =>
          t("aiAssistant.toolApproval.rejected", { toolName: getToolNameDisplayValue(tool) }),
        [ToolApprovalStatus.PENDING]: () => "",
        [ToolApprovalStatus.PERMISSION_DENIED]: () => "",
      };
      return statusesMap[tool.status]();
    };

    const handleAction = async (action: "approve" | "reject", tool: ToolApprovalCall) => {
      if (!sessionId.value) {
        return;
      }

      loadingAction.value = action;
      try {
        await aiAssistantStore.handleAiToolApproval(
          action,
          sessionId.value,
          props.content.approvalRequestId,
          tool.toolExecutionRequestId
        );
      } finally {
        loadingAction.value = null;
      }
    };

    const isActionLoading = (action: "approve" | "reject"): boolean =>
      loadingAction.value === action;

    const displayedToolCalls = computed(() => {
      let hasShownPending = false;

      return props.content.toolCalls.filter((tool) => {
        if (tool.status !== ToolApprovalStatus.PENDING) return true;

        // If we haven't shown a pending one yet, show this one and flip the flag
        if (!hasShownPending) {
          hasShownPending = true;
          return true;
        }

        return false;
      });
    });

    const pendingDisplayedTool = computed(() =>
      displayedToolCalls.value.find((tool) => tool.status === ToolApprovalStatus.PENDING)
    );

    const hasPendingDisplayed = computed(() => !!pendingDisplayedTool.value);

    const pendingOverflowCount = computed(() => {
      const pendingCount = props.content.toolCalls.filter(
        (tool) => tool.status === ToolApprovalStatus.PENDING
      ).length;

      return Math.max(pendingCount - 1, 0);
    });

    const hotkeysHint = computed(() => {
      const hotkeyPlatform = getOSVersion();
      const modifier = hotkeyPlatform === "macos" ? "⌘" : "Ctrl";
      const enterKey = hotkeyPlatform === "macos" ? "↵" : "Enter";
      return t("aiAssistant.toolApproval.hotkeysHint", { modifier, enterKey });
    });

    const handleHotkey = (event: KeyboardEvent) => {
      // Ensure component is mounted and visible before processing hotkeys
      if (!rootElement.value || rootElement.value.offsetParent === null) {
        return;
      }

      const tool = pendingDisplayedTool.value;
      if (!tool || loadingAction.value) {
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        handleAction("reject", tool);
        return;
      }

      if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
        event.preventDefault();
        handleAction("approve", tool);
      }
    };

    onMounted(() => {
      window.addEventListener("keydown", handleHotkey);
    });

    onUnmounted(() => {
      window.removeEventListener("keydown", handleHotkey);
    });

    return {
      rootElement,
      displayedToolCalls,
      getApprovalRows,
      handleAction,
      isPending,
      getStatusClass,
      getStatusText,
      isActionLoading,
      hasPendingDisplayed,
      pendingOverflowCount,
      hotkeysHint,
      ToolApprovalStatus,
    };
  },
});
</script>

<style lang="scss" scoped>
.ai-tool-approval-card {
  --ai-tool-approval-gradient: linear-gradient(
    90deg,
    rgb(var(--v-theme-labels-purple-full)) 0%,
    rgb(var(--v-theme-blue-light)) 100%
  );
  border: 2px solid transparent;
  border-radius: 8px;
  background:
    linear-gradient(rgb(var(--v-theme-white)), rgb(var(--v-theme-white))) padding-box,
    var(--ai-tool-approval-gradient) border-box;
  color: rgb(var(--v-theme-primary));
}

.ai-tool-approval-divider {
  height: 2px;
  background: var(--ai-tool-approval-gradient);
  margin: 12px 0;
}

.ai-tool-approval-approved {
  border: 2px solid rgb(var(--v-theme-green-base));
  background: rgb(var(--v-theme-white));
}

.ai-tool-approval-neutral {
  border: 2px solid rgb(var(--v-theme-indigo-pale));
  background: rgb(var(--v-theme-white));
}

i.icon.icon-insights-xs {
  &::before {
    color: inherit !important;
  }
}
</style>
